import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./cxm-form.scss";

interface CxmPageShellProps {
  /** Distinguishes the two flows: their forms are not the same height. */
  flow: "deposit" | "withdrawal";
  /** While true the shell holds its floor instead of measuring. */
  loading: boolean;
  children: React.ReactNode;
}

/**
 * The one box the CXM route lives in, loading and loaded alike.
 *
 * The host modal does not measure the iframe: the widget reports
 * `document.body.scrollHeight` to it through `useSendHeightToParent`, so the
 * modal resizes once for every distinct height the document passes through. On
 * this route that was three — the bare pre-route loader, the branded pane, and
 * then the form — which is the stepping and the momentary double height that
 * were reported.
 *
 * A fixed floor cannot fix the last step, because the form's height is not
 * known until it renders and the two flows differ. So the shell remembers it:
 * once the form is up its height is measured and kept, and the next open holds
 * the document at that height while the logo plays. The first open of a flow
 * still grows once, from the default below to the real height.
 */
const STORAGE_PREFIX = "cxm-page-height:";
/**
 * Measured, not guessed: the payout form renders at 410px and holds that
 * across viewport widths, so a taller floor is visible as dead space under
 * the logo while the route loads. The deposit form is shorter and settles on
 * its own value after the first open of a session.
 */
const DEFAULT_FLOOR = 410;

/** Ignore absurd measurements rather than remember them. */
const MIN_REMEMBERED = 240;
const MAX_REMEMBERED = 1200;

const readFloor = (flow: string): number => {
  try {
    const stored = Number(window.sessionStorage.getItem(STORAGE_PREFIX + flow));
    if (stored >= MIN_REMEMBERED && stored <= MAX_REMEMBERED) return stored;
  } catch {
    // Private mode, blocked storage — the default floor is still correct.
  }
  return DEFAULT_FLOOR;
};

const CxmPageShell: React.FC<CxmPageShellProps> = ({ flow, loading, children }): JSX.Element => {
  const [floor] = useState(() => readFloor(flow));
  const ref = useRef<HTMLDivElement>(null);

  // Layout effect: measure before the browser paints, so the remembered value
  // never includes a frame of the form mid-transition.
  useLayoutEffect(() => {
    if (loading || !ref.current) return;
    const measured = Math.round(ref.current.scrollHeight);
    if (measured < MIN_REMEMBERED || measured > MAX_REMEMBERED) return;
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + flow, String(measured));
    } catch {
      // Nothing to do — next open simply falls back to the default.
    }
  }, [flow, loading, children]);

  // Once the form is on screen the shell must not keep a floor taller than the
  // content, or it leaves dead space under the form.
  const [released, setReleased] = useState(false);
  useEffect(() => {
    if (!loading) setReleased(true);
  }, [loading]);

  return (
    <div className="cxm-page" ref={ref} style={{ minHeight: released ? undefined : floor }}>
      {children}
    </div>
  );
};

export default CxmPageShell;
